import { Button } from "@/components/ui/button";
import { FictionalVehicle, fmt } from "@/hooks/useVehicleSim";
import { ArrowLeft, ArrowRight, ThumbsUp, ThumbsDown, User } from "lucide-react";
import vehicleImages from "./vehicleImages";

interface Props {
  vehicle: FictionalVehicle;
  vehicles: FictionalVehicle[];
  onContinue: () => void;
  onBack: () => void;
}

function RatingBar({ label, value }: { label: string; value: number }) {
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-semibold text-foreground">{value}/10</span>
      </div>
      <div className="h-2 rounded-full bg-muted overflow-hidden">
        <div className="h-full rounded-full bg-primary transition-all duration-500" style={{ width: `${value * 10}%` }} />
      </div>
    </div>
  );
}

export default function VehicleDetail({ vehicle: v, vehicles, onContinue, onBack }: Props) {
  return (
    <div className="space-y-6">
      <div className="glass-card glow-border rounded-2xl overflow-hidden">
        {vehicleImages[v.vehicle_key] && (
          <img src={vehicleImages[v.vehicle_key]} alt={v.vehicle_name} className="w-full h-52 object-cover" />
        )}
        <div className="p-6 space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="font-heading text-lg font-bold text-foreground">{v.vehicle_name}</h2>
              <p className="text-sm text-muted-foreground capitalize">{v.vehicle_type}</p>
            </div>
            <p className="text-xl font-heading font-bold text-foreground">{fmt(v.purchase_price)}</p>
          </div>

          <p className="text-sm text-muted-foreground">{v.description}</p>

          <div className="space-y-2.5">
            <RatingBar label="Practicality" value={v.practicality_rating} />
            <RatingBar label="Status Appeal" value={v.status_rating} />
            <RatingBar label="Efficiency" value={v.efficiency_rating} />
            <RatingBar label="Reliability" value={v.reliability_rating} />
          </div>

          <div className="grid grid-cols-3 gap-2 text-xs">
            <div className="glass-card p-2.5 text-center">
              <p className="text-muted-foreground">Insurance</p>
              <p className="font-bold text-foreground">{fmt(v.insurance_monthly)}/mo</p>
            </div>
            <div className="glass-card p-2.5 text-center">
              <p className="text-muted-foreground">Maintenance</p>
              <p className="font-bold text-foreground">{fmt(v.maintenance_monthly)}/mo</p>
            </div>
            <div className="glass-card p-2.5 text-center">
              <p className="text-muted-foreground">Fuel</p>
              <p className="font-bold text-foreground">{fmt(v.fuel_monthly)}/mo</p>
            </div>
          </div>

          <div className="space-y-3 pt-2">
            <div className="flex gap-2 items-start">
              <ThumbsUp className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
              <p className="text-xs text-muted-foreground">{v.pros}</p>
            </div>
            <div className="flex gap-2 items-start">
              <ThumbsDown className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
              <p className="text-xs text-muted-foreground">{v.cons}</p>
            </div>
            <div className="flex gap-2 items-start">
              <User className="w-4 h-4 text-primary shrink-0 mt-0.5" />
              <p className="text-xs text-muted-foreground"><span className="font-semibold text-foreground">Best for:</span> {v.ideal_buyer}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Comparison */}
      <div className="space-y-2">
        <h3 className="font-heading text-sm font-semibold text-foreground">Quick Comparison</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 text-muted-foreground font-medium">Vehicle</th>
                <th className="text-right py-2 text-muted-foreground font-medium">Price</th>
                <th className="text-right py-2 text-muted-foreground font-medium">Ins/mo</th>
                <th className="text-right py-2 text-muted-foreground font-medium">Maint/mo</th>
              </tr>
            </thead>
            <tbody>
              {vehicles.map(veh => (
                <tr key={veh.vehicle_key} className={`border-b border-border/50 ${veh.vehicle_key === v.vehicle_key ? 'bg-primary/5' : ''}`}>
                  <td className={`py-2 ${veh.vehicle_key === v.vehicle_key ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>{veh.vehicle_name}</td>
                  <td className="py-2 text-right text-foreground">{fmt(veh.purchase_price)}</td>
                  <td className="py-2 text-right text-foreground">{fmt(veh.insurance_monthly)}</td>
                  <td className="py-2 text-right text-foreground">{fmt(veh.maintenance_monthly)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex gap-3">
        <Button variant="outline" onClick={onBack} className="gap-2"><ArrowLeft className="w-4 h-4" /> Back</Button>
        <Button onClick={onContinue} className="flex-1 gap-2">Continue <ArrowRight className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}
